import Modal from 'components/Modal'
import client from 'utils/socket'
import useRequest from './useRequest'
import Typography from 'components/Typography'
import iconSuccess from 'assets/svg/tick-circle.svg'
import { t } from 'utils/localization'
import { alerts } from 'components/Toast/Toast'
import { useProfile } from './useContext'
import { useEffect, useMemo, useState } from 'react'

const usePayment = () => {
  const profile = useProfile()

  const [isLoading, setIsLoading] = useState(false)
  const [paymentUrl, setPaymentUrl] = useState<string>('')
  const [showSuccess, setShowSuccess] = useState<boolean>(false)
  const [paymentSuccessed, setPaymentSuccessed] = useState<boolean>(false)

  const isFirstPurchase =
    !profile.user?.authData.planIsSelected &&
    profile.user?.cloudData.plan.id === 'free'

  const planRequest = useRequest('/plan', {
    method: 'PUT',
    onSuccess() {
      profile.setUser((user) => {
        if (!user) {
          return null
        }
        return {
          ...user,
          authData: {
            ...user.authData,
            planIsSelected: true
          }
        }
      })
    }
  })

  const payForPlan = async (planId: string) => {
    try {
      setIsLoading(true)

      if (planId.includes('free')) {
        if (!isFirstPurchase) {
          await client.cancelXsollaSubscription()
        }
        planRequest.request()
        profile.setShowPlanModal(false)
        return
      }

      const isSandbox = process.env.REACT_APP_PAYMENT_URL?.includes('sandbox')
      const token = await client.getXsollaPaymentToken(planId, isSandbox)

      setPaymentUrl(
        `${process.env.REACT_APP_PAYMENT_URL}/paystation3/?access_token=${token}`
      )
    } catch (error) {
      alerts.error('unexpected-error')
      console.error({ error })
    } finally {
      setIsLoading(false)
    }
  }

  const ModalContent = useMemo(
    () => (
      <iframe
        src={paymentUrl}
        title='payment'
        sandbox='allow-same-origin allow-scripts allow-popups allow-forms'
        className='b-payment'
      />
    ),
    [paymentUrl]
  )

  const ModalComponent = useMemo(
    () => (
      <Modal
        isOpen={!!paymentUrl}
        onClose={() => {
          if (showSuccess || paymentSuccessed) {
            profile.setShowPlanModal(false)
          }
          setPaymentUrl('')
          setShowSuccess(false)
          setPaymentSuccessed(false)
        }}
      >
        {showSuccess ? (
          <div className='b-success'>
            <img src={iconSuccess} alt='' />
            <Typography weight='semi' size='lg'>
              {t('successfull-payment')}
            </Typography>
          </div>
        ) : (
          ModalContent
        )}
      </Modal>
    ),
    [profile, paymentUrl, showSuccess, paymentSuccessed, ModalContent]
  )

  useEffect(() => {
    function receiveMessage(event: any) {
      if (event.origin === process.env.REACT_APP_PAYMENT_URL) {
        try {
          const data = JSON.parse(event.data)

          if (data.command === 'change-status' && data.data?.value === 'done') {
            setPaymentSuccessed(true)
          } else if (data.action === 'finish') {
            if (paymentSuccessed) {
              setShowSuccess(true)
            } else {
              setPaymentUrl('')
            }
          }
        } catch (error) {
          console.error({ error })
        }
      }
    }

    window.addEventListener('message', receiveMessage)

    return () => {
      window.removeEventListener('message', receiveMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planRequest])

  return {
    isLoading: isLoading || planRequest.isLoading,
    payForPlan,
    ModalComponent
  }
}

export default usePayment
